(() => {
  const planId = item => String(item.selling_plan_allocation?.selling_plan?.id || '');
  const variantId = item => String(item.variant_id || item.id || '');

  function reconciliation(cart, config) {
    const allowed = new Set((config.planIds || []).map(String).filter(id => Number.isSafeInteger(Number(id)) && Number(id) > 0));
    const helper = String(config.variantId || '');
    const canAdd = config.enabled === true && config.available === true &&
      config.price === 0 && config.requiresShipping === false &&
      Number.isSafeInteger(Number(helper)) && Number(helper) > 0;
    const merchandise = (cart.items || []).filter(item => variantId(item) !== helper && item.quantity > 0);
    const parentKey = merchandise[merchandise.length - 1]?.key;
    const desired = new Set(merchandise.filter(item => allowed.has(planId(item))).map(planId));
    const retained = new Set();
    const updates = {};
    for (const item of cart.items || []) {
      if (variantId(item) !== helper) continue;
      const plan = planId(item);
      const keep = canAdd && desired.has(plan) && !retained.has(plan) &&
        item.price === 0 && item.final_line_price === 0 &&
        item.parent_relationship?.parent_key === parentKey;
      if (keep) retained.add(plan);
      const quantity = keep ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    }
    const adds = canAdd && parentKey ? [...desired].filter(plan => !retained.has(plan)).map(plan => ({
      id: Number(helper), quantity: 1, selling_plan: Number(plan),
      properties: { _sp_subscription_companion: '1' },
      parent_line_key: parentKey,
    })) : [];
    return { updates, adds };
  }

  function createManager(config, fetcher, routes, onChange = () => {}) {
    let tail = Promise.resolve();
    let pending = 0;
    const effectiveConfig = { ...config };
    const json = async (url, init = {}) => {
      const response = await fetcher(url, { credentials: 'same-origin', ...init });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        const error = new Error(data.description || data.message || 'Cart update failed');
        error.cartResponse = data;
        throw error;
      }
      return data;
    };
    const post = (url, body) => json(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cartUrl = routes.cart_url.replace(/\/$/, '');
    const getCart = () => json(`${cartUrl}.js`, { cache: 'no-store' });
    const enqueue = operation => {
      pending++;
      const task = tail.then(operation);
      tail = task.catch(() => {}).finally(() => { pending--; });
      return task;
    };
    const sync = async () => {
      let cart = await getCart();
      let changed = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        // A product edit can make the page's rendered price stale. Remove an unsafe
        // companion and stop adding it for this page session.
        if ((cart.items || []).some(item => variantId(item) === String(config.variantId) &&
            (item.price !== 0 || item.final_line_price !== 0 || item.requires_shipping === true))) {
          effectiveConfig.enabled = false;
        }
        const action = reconciliation(cart, effectiveConfig);
        if (Object.keys(action.updates).length) {
          cart = await post(routes.cart_update_url, { updates: action.updates });
          changed = true;
          continue;
        }
        if (action.adds.length) {
          await post(routes.cart_add_url, { items: action.adds });
          cart = await getCart();
          changed = true;
          continue;
        }
        return { cart, changed };
      }
      const remaining = reconciliation(cart, effectiveConfig);
      if (Object.keys(remaining.updates).length || remaining.adds.length) {
        throw new Error('Subscription cart did not settle');
      }
      return { cart, changed };
    };
    const sectionsFor = async (sections, sectionsUrl) => {
      const ids = [...new Set(sections || [])].filter(Boolean).slice(0, 5);
      if (!ids.length) return {};
      const url = sectionsUrl || cartUrl;
      const result = await json(`${url}${url.includes('?') ? '&' : '?'}sections=${encodeURIComponent(ids.join(','))}`, { cache: 'no-store' });
      if (ids.some(id => typeof result[id] !== 'string')) throw new Error('Cart sections unavailable');
      return result;
    };
    return {
      reconciliation: cart => reconciliation(cart, config),
      get pending() { return pending; },
      request: (url, init, options = {}) => enqueue(async () => {
        const original = await json(url, init);
        try {
          const { cart, changed } = await sync();
          const sections = changed
            ? await sectionsFor(options.sections, options.sectionsUrl)
            : original.sections;
          return { ...original, ...cart, id: original.id, sections };
        } catch (error) {
          // A display-only companion must not turn a successful product add into a retry.
          onChange({ error, refresh: true });
          return original;
        }
      }).catch(error => {
        if (error.cartResponse) return error.cartResponse;
        throw error;
      }),
      refresh: () => enqueue(async () => {
        const result = await sync();
        if (result.changed) await onChange({ cart: result.cart, refresh: true });
        return result.cart;
      }),
      settled: () => tail,
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { reconciliation, createManager };
  if (typeof window === 'undefined' || !window.SPSubscriptionCompanionConfig?.enabled) return;
  const notify = ({ cart, refresh, error }) => {
    if (error) console.warn('Subscription cart display could not be refreshed.', error);
    if (refresh && typeof publish === 'function') {
      return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'subscription-companion', cartData: cart });
    }
  };
  const manager = createManager(window.SPSubscriptionCompanionConfig, window.fetch.bind(window), window.routes, notify);
  window.SPSubscriptionCompanion = manager;

  let resumingCheckout = false;
  let checkingOut = false;
  document.addEventListener('submit', async event => {
    if (resumingCheckout || !event.target.matches('form[action*="/cart"]') || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    if (checkingOut) return;
    checkingOut = true;
    const formId = event.target.id;
    const buttonId = event.submitter.id;
    const originalForm = event.target;
    const originalButton = event.submitter;
    originalButton.disabled = true;
    try {
      await manager.refresh();
      // Cart rendering can replace the form and its externally associated button.
      const form = document.getElementById(formId) || originalForm;
      const button = document.getElementById(buttonId) || originalButton;
      if (!form.isConnected || !button.isConnected || button.form !== form) throw new Error('Cart refreshed; please retry checkout');
      button.disabled = false;
      resumingCheckout = true;
      try { form.requestSubmit(button); }
      finally { resumingCheckout = false; }
    } catch (error) {
      notify({ error });
      const form = document.getElementById(formId) || originalForm;
      let message = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (!message) {
        message = document.createElement('p');
        message.setAttribute('role', 'alert');
        form.appendChild(message);
      }
      message.textContent = window.cartStrings.error;
      message.setAttribute('tabindex', '-1');
      message.focus();
    } finally {
      checkingOut = false;
      originalButton.disabled = false;
      const button = document.getElementById(buttonId);
      if (button) button.disabled = false;
    }
  }, true);
  manager.refresh().catch(error => notify({ error }));
})();
